import { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthContext } from '../auth/AuthContext';
import { getMyIssues, getIssueById, updateIssueStatus } from '../api/issueApi';
import { getVisibleUsers } from '../api/userApi';
import PendingInvitationsCard from '../components/PendingInvitationsCard';
import { getPerformanceReviewStatus } from '../api/performanceReviewApi';
import toast from 'react-hot-toast';
import IssueForm from '../components/IssueForm';
import WorkItemCreateModal from '../components/WorkItemCreateModal';
import { issueFormModalPanelStyle } from '../constants/issueFormModal';
import TaskDetailModal from '../components/TaskDetailModal';
import PerformanceReviewModal from '../components/PerformanceReviewModal';
import TeamSopViewDialog from '../components/TeamSopViewDialog';
import { canSetupPerformanceReviewCycle } from '../components/PerformanceReviewCycleSetup';
import { IssueDetailModal } from './Issues';
import { getWorkflowStatusOptions } from '../constants/issueStatuses';
import { DEFAULT_DEPARTMENT } from '../constants/departments';
import { isQaTesterRole } from '../utils/roleUtils';
import { COLORS as C, RADIUS } from '../constants/ui';
import TabGroup from '../components/ui/TabGroup';
import { SkeletonList } from '../components/ui/Skeleton';
import {
  isTerminalStatus,
  sortRowsForReview,
  sortBugsByTimestamp,
  taskRowsOnly,
  bugRowsOnly,
  CLOSED_BUG_STATUSES,
  WorkItemCard,
  StatBox,
  buildMyTasksHierarchy,
  StoryChecklistTaskRow,
  StoryGroupHeaderRow,
} from '../components/profileWorkItems';

const VALID_WORK_TABS = new Set(['tasks', 'bugs']);

const TASK_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'Open', label: 'Open' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Testing', label: 'In Review' },
  { value: 'Done', label: 'Done' },
];

const BUG_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'Open', label: 'Open' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Testing', label: 'In Review' },
  { value: 'Blocked', label: 'Blocked' },
  { value: 'fixed', label: 'Fixed / closed' },
  { value: 'Reopened', label: 'Reopened' },
];

const filterTasksByStatus = (rows, statusFilter) => {
  if (statusFilter === 'all') return rows;
  if (statusFilter === 'Done') return rows.filter((row) => row.status === 'Done');
  return rows.filter((row) => row.status === statusFilter);
};

const filterBugsByStatus = (rows, statusFilter) => {
  if (statusFilter === 'all') return rows;
  if (statusFilter === 'fixed') {
    return rows.filter(
      (b) =>
        b.status === 'Fixed' ||
        b.status === 'Closed' ||
        CLOSED_BUG_STATUSES.has(b.status),
    );
  }
  return rows.filter((b) => b.status === statusFilter);
};

const MyTasks = () => {
  const { role, user, sessionReady } = useContext(AuthContext);
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTester = isQaTesterRole(role);
  const initialTab = (() => {
    const fromUrl = searchParams.get('tab');
    if (VALID_WORK_TABS.has(fromUrl)) return fromUrl;
    return isTester ? 'bugs' : 'tasks';
  })();
  const [workSubTab, setWorkSubTab] = useState(initialTab);
  const [sprintFilter, setSprintFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bugStatusFilter, setBugStatusFilter] = useState('all');
  const [createMode, setCreateMode] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [storyReturnIssue, setStoryReturnIssue] = useState(null);
  const [prOpen, setPrOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);

  const { data: prStatus } = useQuery({
    queryKey: ['performance-review-status'],
    queryFn: () => getPerformanceReviewStatus(),
    staleTime: 30_000,
    retry: false,
  });

  const showPerformanceReview = prStatus?.enabled === true;

  const prCycleParams = useMemo(() => {
    const d = new Date();
    const fallbackQ = Math.floor(d.getMonth() / 3) + 1;
    const fallbackY = d.getFullYear();
    const c = prStatus?.cycle;
    if (c && c.year != null && c.quarter != null) {
      return { year: Number(c.year), quarter: Number(c.quarter) };
    }
    return { year: fallbackY, quarter: fallbackQ };
  }, [prStatus?.cycle]);

  const { data: issues = [], isLoading } = useQuery({
    queryKey: ['my-issues', user?.id],
    queryFn: () => getMyIssues().then((r) => r.data),
    enabled: sessionReady && !!user?.id,
    refetchInterval: 30000,
  });

  const { data: assignUsers = [] } = useQuery({
    queryKey: ['users-visible-my-tasks'],
    queryFn: () => getVisibleUsers().then((r) => r.data || []),
    staleTime: 60_000,
  });

  const selectWorkTab = useCallback(
    (tabId) => {
      if (!VALID_WORK_TABS.has(tabId)) return;
      setWorkSubTab(tabId);
      setSearchParams({ tab: tabId }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const fromUrl = searchParams.get('tab');
    if (VALID_WORK_TABS.has(fromUrl)) {
      setWorkSubTab(fromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isTester || searchParams.get('tab')) return;
    setSearchParams({ tab: 'bugs' }, { replace: true });
  }, [isTester, searchParams, setSearchParams]);

  const bySprint = useMemo(() => {
    if (sprintFilter === 'all') return issues;
    if (sprintFilter === 'none') return issues.filter((i) => i.sprint_id == null);
    return issues.filter((i) => String(i.sprint_id) === sprintFilter);
  }, [issues, sprintFilter]);

  /** Dropdown lists only active sprints (plus backlog). Closed/released sprints stay visible under "All sprints". */
  const sprintOptions = useMemo(() => {
    const m = new Map();
    issues.forEach((i) => {
      const sid = i.sprint_id;
      const key = sid == null ? 'none' : String(sid);
      if (sid != null && String(i.sprint_status || '').trim() !== 'Active') return;
      if (!m.has(key)) {
        m.set(key, { value: key, label: sid == null ? 'No sprint' : i.sprint_name || `Sprint ${sid}` });
      }
    });
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [issues]);

  useEffect(() => {
    if (sprintFilter === 'all') return;
    if (sprintOptions.some((o) => o.value === sprintFilter)) return;
    setSprintFilter('all');
  }, [sprintFilter, sprintOptions]);

  const tasks = useMemo(
    () => sortRowsForReview(filterTasksByStatus(taskRowsOnly(bySprint), statusFilter)),
    [bySprint, statusFilter],
  );
  const bugs = useMemo(
    () => sortBugsByTimestamp(filterBugsByStatus(bugRowsOnly(bySprint), bugStatusFilter)),
    [bySprint, bugStatusFilter],
  );

  const sprintScopedTasks = useMemo(() => taskRowsOnly(bySprint), [bySprint]);
  const openTasks = sprintScopedTasks.filter((t) => t.status === 'Open').length;
  const inProgressTasks = sprintScopedTasks.filter((t) => t.status === 'In Progress').length;
  const testingTasks = sprintScopedTasks.filter((t) => t.status === 'Testing').length;
  const inFlightTasks = sprintScopedTasks.filter((t) => !isTerminalStatus(t.status)).length;

  const bugOpen = bugs.filter((b) => b.status === 'Open').length;
  const bugBlockers = bugs.filter((b) => (b.priority || '') === 'Blocker').length;
  const bugFixed = bugs.filter((b) => b.status === 'Fixed' || b.status === 'Done').length;
  const bugClosed = bugs.filter(
    (b) => b.status === 'Closed' || CLOSED_BUG_STATUSES.has(b.status),
  ).length;
  const bugPipelineOther = bugs.filter((b) =>
    ['In Progress', 'Testing', 'Blocked'].includes(b.status),
  ).length;

  const list = workSubTab === 'bugs' ? bugs : tasks;

  const taskHierarchy = useMemo(
    () => (workSubTab === 'tasks' ? buildMyTasksHierarchy(tasks) : null),
    [workSubTab, tasks],
  );

  /**
   * Prev/Next order for the detail modal — mirrors exactly what is on screen,
   * row for row: a single-task story is represented by its task (the row that
   * is actually rendered and clickable), everything else by the story id.
   */
  const taskNavigationIds = useMemo(() => {
    if (!taskHierarchy) return null;
    const ids = [];
    taskHierarchy.storyGroups.forEach(({ story, children }) => {
      ids.push(children.length === 1 ? children[0].issueid : story.issueid);
    });
    taskHierarchy.orphanTasks.forEach((row) => ids.push(row.issueid));
    taskHierarchy.other.forEach((row) => ids.push(row.issueid));
    return ids;
  }, [taskHierarchy]);

  const cachedSelectedIssue = useMemo(() => {
    if (!selectedId) return null;
    return issues.find((i) => String(i.issueid) === String(selectedId)) || null;
  }, [issues, selectedId]);

  const { data: fetchedSelectedIssue, isLoading: loadingSelectedIssue } = useQuery({
    queryKey: ['issue', selectedId],
    queryFn: () => getIssueById(selectedId).then((r) => r.data),
    enabled: !!selectedId,
    staleTime: 15_000,
  });

  const selectedIssue = fetchedSelectedIssue ?? cachedSelectedIssue;

  const isStoryOrTaskIssue = (issue) =>
    issue?.issuetype === 'Story' || issue?.issuetype === 'Task';

  const parentIssueForModal = useMemo(() => {
    if (!selectedIssue?.parent_id) return null;
    if (
      storyReturnIssue
      && String(storyReturnIssue.issueid) === String(selectedIssue.parent_id)
    ) {
      return storyReturnIssue;
    }
    const parent = issues.find((i) => String(i.issueid) === String(selectedIssue.parent_id));
    return parent?.issuetype === 'Story' || parent?.issuetype === 'Epic' ? parent : null;
  }, [selectedIssue, storyReturnIssue, issues]);

  const hierarchyStoryChildren = useMemo(() => {
    if (!selectedId || !taskHierarchy) return null;
    const group = taskHierarchy.storyGroups.find(
      (g) => String(g.story.issueid) === String(selectedId),
    );
    return group?.children?.length ? group.children : null;
  }, [selectedId, taskHierarchy]);

  const workTabs = useMemo(() => {
    const tasksTab = { id: 'tasks', label: `Tasks (${tasks.length})` };
    const bugsTab = {
      id: 'bugs',
      label: isTester ? `My Bugs (${bugs.length})` : `Bugs (${bugs.length})`,
    };
    return isTester ? [bugsTab, tasksTab] : [tasksTab, bugsTab];
  }, [isTester, tasks.length, bugs.length]);

  const createTaskDefaults = useMemo(() => {
    if (sprintFilter !== 'all' && sprintFilter !== 'none') {
      const sample = issues.find((i) => String(i.sprint_id) === sprintFilter);
      return {
        projectId: sample?.projectid ? String(sample.projectid) : '',
        sprintId: sprintFilter,
      };
    }
    const activeIssue = issues.find(
      (i) => String(i.sprint_status || '').trim() === 'Active' && i.sprint_id != null,
    );
    if (activeIssue) {
      return {
        projectId: String(activeIssue.projectid),
        sprintId: String(activeIssue.sprint_id),
      };
    }
    return { projectId: '', sprintId: '' };
  }, [issues, sprintFilter]);

  useEffect(() => {
    if (!selectedId && !createMode) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selectedId, createMode]);

  const handleIssueStatusChanged = () => {
    qc.invalidateQueries({ queryKey: ['my-issues'] });
    qc.invalidateQueries({ queryKey: ['board'] });
    qc.invalidateQueries({ queryKey: ['sprints'] });
  };

  const boardDepartment = user?.department || DEFAULT_DEPARTMENT;
  const workflowOptions = useMemo(
    () => getWorkflowStatusOptions(boardDepartment),
    [boardDepartment],
  );

  const handleTaskStatusChange = useCallback(async (issueId, status) => {
    try {
      const resolution = status === 'Fixed' ? 'Fixed' : undefined;
      await updateIssueStatus(issueId, status, resolution);
      qc.invalidateQueries({ queryKey: ['my-issues'] });
      qc.invalidateQueries({ queryKey: ['board'] });
      qc.invalidateQueries({ queryKey: ['sprints'] });
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  }, [qc]);

  /**
   * Patch statuses straight into the cached list so a tick lands instantly
   * instead of after the round-trip. Returns the previous cache so a failed
   * request can put it back. Mirrors what the Story checklist and Daily
   * Notebook already do; this page was the one that still waited.
   */
  const patchIssueStatuses = useCallback((ids, status) => {
    const key = ['my-issues', user?.id];
    const previous = qc.getQueryData(key);
    if (!Array.isArray(previous)) return null;
    const idSet = new Set(ids.map(String));
    qc.setQueryData(
      key,
      previous.map((i) => (idSet.has(String(i.issueid)) ? { ...i, status } : i)),
    );
    return { key, previous };
  }, [qc, user?.id]);

  /** Checklist-row checkbox — same status update path, no toast per-tick (bulk-friendly) and tracks in-flight rows so the row can show a busy state. */
  const [togglingTaskIds, setTogglingTaskIds] = useState([]);
  const handleChecklistToggle = useCallback(async (issueId, status) => {
    setTogglingTaskIds((prev) => [...prev, issueId]);
    // Stop an in-flight refetch from overwriting the patch mid-request.
    await qc.cancelQueries({ queryKey: ['my-issues'] });
    const snapshot = patchIssueStatuses([issueId], status);
    try {
      const resolution = status === 'Fixed' ? 'Fixed' : undefined;
      await updateIssueStatus(issueId, status, resolution);
      qc.invalidateQueries({ queryKey: ['my-issues'] });
      qc.invalidateQueries({ queryKey: ['board'] });
      qc.invalidateQueries({ queryKey: ['sprints'] });
    } catch (err) {
      if (snapshot) qc.setQueryData(snapshot.key, snapshot.previous);
      toast.error(err.response?.data?.message || 'Failed to update task');
    } finally {
      setTogglingTaskIds((prev) => prev.filter((id) => id !== issueId));
    }
  }, [qc, patchIssueStatuses]);

  /** Story-header checkbox — closes every still-open child task in one action. */
  const [markingAllStoryIds, setMarkingAllStoryIds] = useState([]);
  const handleMarkAllStoryTasksDone = useCallback(async (storyId, children) => {
    const openChildren = children.filter((t) => !isTerminalStatus(t.status));
    if (openChildren.length === 0) return;
    setMarkingAllStoryIds((prev) => [...prev, storyId]);
    await qc.cancelQueries({ queryKey: ['my-issues'] });
    // Strike the whole group through at once — waiting for N requests before
    // anything moves is the most noticeable delay on this page.
    const snapshot = patchIssueStatuses(openChildren.map((t) => t.issueid), 'Closed');
    try {
      const results = await Promise.allSettled(
        openChildren.map((t) => updateIssueStatus(t.issueid, 'Closed')),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      // A partial failure makes the local guess wrong for some rows; the
      // refetch below is the correction, so don't roll the whole group back.
      qc.invalidateQueries({ queryKey: ['my-issues'] });
      qc.invalidateQueries({ queryKey: ['board'] });
      qc.invalidateQueries({ queryKey: ['sprints'] });
      if (failed > 0) {
        toast.error(`${openChildren.length - failed} marked done, ${failed} failed`);
      } else {
        toast.success(`${openChildren.length} task${openChildren.length > 1 ? 's' : ''} marked done`);
      }
    } catch (err) {
      if (snapshot) qc.setQueryData(snapshot.key, snapshot.previous);
      toast.error(err.response?.data?.message || 'Failed to mark tasks done');
    } finally {
      setMarkingAllStoryIds((prev) => prev.filter((id) => id !== storyId));
    }
  }, [qc, patchIssueStatuses]);

  return (
    <div style={{ padding: '1.5rem 2rem', background: C.sunken, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: C.ink }}>
            My Tasks
          </h1>
          <p style={{ margin: '0.2rem 0 0', color: C.inkSoft, fontSize: '0.9rem' }}>
            {isTester
              ? 'My Bugs is your reported-defect queue; the Tasks tab shows anything assigned to you.'
              : 'Work assigned to you — switch between Tasks and Bugs below.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setDocumentsOpen(true)}
            style={{
              background: '#fff',
              color: C.done,
              border: `2px solid ${C.done}`,
              borderRadius: 8,
              padding: '0.6rem 1.2rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Documents
          </button>
          {showPerformanceReview ? (
            <button
              type="button"
              onClick={() => setPrOpen(true)}
              style={{
                background: '#fff',
                color: C.accentInk,
                border: `2px solid ${C.accentInk}`,
                borderRadius: 8,
                padding: '0.6rem 1.2rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Performance review
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setCreateMode(isTester ? 'bug' : 'task')}
            style={{
              background: C.accentInk,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '0.6rem 1.2rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {isTester ? '+ Log Bug' : '+ Add Task'}
          </button>
          {isTester ? (
            <button
              type="button"
              onClick={() => setCreateMode('task')}
              style={{
                background: C.done,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '0.6rem 1.2rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + Add Task
            </button>
          ) : null}
        </div>
      </div>

      <PendingInvitationsCard />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        {workSubTab === 'bugs' ? (
          <>
            <StatBox label="Open" value={bugOpen} color={C.accent} />
            <StatBox label="Blocker" value={bugBlockers} color={C.danger} />
            <StatBox label="Fixed" value={bugFixed} color={C.done} />
            <StatBox label="Closed" value={bugClosed} color={C.inkSoft} />
          </>
        ) : (
          <>
            <StatBox label="Open" value={openTasks} color={C.accent} />
            <StatBox label="In Progress" value={inProgressTasks} color={C.accent} />
            <StatBox label="In Review" value={testingTasks} color={C.warn} />
            <StatBox label="In flight" value={inFlightTasks} color={C.done} />
          </>
        )}
      </div>

      {workSubTab === 'bugs' && bugPipelineOther > 0 && (
        <div
          style={{
            fontSize: '0.78rem',
            color: C.inkSoft,
            marginBottom: '1rem',
            fontWeight: 600,
          }}
        >
          Also in workflow: {bugPipelineOther} in In Progress / In Review / Blocked
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: '1rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: C.ink, margin: 0 }}>
            {workSubTab === 'bugs' && isTester ? 'My Bugs' : workSubTab === 'bugs' ? 'Bugs' : 'Tasks'}
          </h2>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: C.inkFaint }}>
            {workSubTab === 'bugs'
              ? isTester
                ? 'Defects you reported — click a row for details'
                : 'Bug work items assigned to you'
              : 'Tasks assigned to you — click a row for details'}
          </p>
        </div>
        {/* Was a white-pill-on-grey group, the inverse of Tracker's. The
            counts already lived in the labels, so they move to the chip. */}
        <TabGroup
          tabs={workTabs.map((t) => {
            const m = /^(.*?)\s*\((\d+)\)\s*$/.exec(t.label);
            return m ? { key: t.id, label: m[1], count: Number(m[2]) } : { key: t.id, label: t.label };
          })}
          value={workSubTab}
          onChange={selectWorkTab}
        />
      </div>

      {issues.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.25rem',
          }}
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.82rem',
              color: C.inkSoft,
              fontWeight: 600,
            }}
          >
            Sprint
            <select
              value={sprintFilter}
              onChange={(e) => setSprintFilter(e.target.value)}
              style={{
                padding: '0.4rem 0.65rem',
                borderRadius: 8,
                border: `1px solid ${C.line}`,
                fontSize: '0.82rem',
                fontWeight: 600,
                color: C.ink,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              <option value="all">All sprints</option>
              {sprintOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {workSubTab === 'tasks' && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.82rem',
                color: C.inkSoft,
                fontWeight: 600,
              }}
            >
              Status
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '0.4rem 0.65rem',
                  borderRadius: 8,
                  border: `1px solid ${C.line}`,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: C.ink,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {TASK_STATUS_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {workSubTab === 'bugs' && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.82rem',
                color: C.inkSoft,
                fontWeight: 600,
              }}
            >
              Status
              <select
                value={bugStatusFilter}
                onChange={(e) => setBugStatusFilter(e.target.value)}
                style={{
                  padding: '0.4rem 0.65rem',
                  borderRadius: 8,
                  border: `1px solid ${C.line}`,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: C.ink,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {BUG_STATUS_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {isLoading ? (
        <SkeletonList rows={5} height={68} label="Loading your work" />
      ) : list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: C.inkFaint }}>
          {workSubTab === 'bugs'
            ? bugStatusFilter !== 'all'
              ? 'No bugs match the selected status.'
              : isTester
                ? 'No bugs in My Bugs for this sprint filter.'
                : 'No bugs in this sprint filter.'
            : statusFilter !== 'all'
              ? 'No tasks match the selected status.'
              : 'No tasks in this sprint filter.'}
        </div>
      ) : workSubTab === 'tasks' && taskHierarchy ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {taskHierarchy.storyGroups.map(({ story, children }) => {
            // A story with exactly one task (the common Daily Notebook case)
            // repeats the same line twice — the story header and its lone
            // child read almost identically. Show just the task, using the
            // same compact checklist row a multi-task group's children use:
            // it is the actionable unit, and the checkbox is the fast path
            // to close it without opening the detail modal at all.
            if (children.length === 1) {
              const [only] = children;
              return (
                <StoryChecklistTaskRow
                  key={`story-${story.issueid}`}
                  task={only}
                  onOpen={(id) => setSelectedId(String(id))}
                  onStatusChange={handleChecklistToggle}
                  isToggling={togglingTaskIds.includes(only.issueid)}
                />
              );
            }
            const doneCount = children.filter((t) => isTerminalStatus(t.status)).length;
            return (
              <div key={`story-${story.issueid}`}>
                <StoryGroupHeaderRow
                  story={story}
                  doneCount={doneCount}
                  totalCount={children.length}
                  onOpen={(id) => setSelectedId(String(id))}
                  onMarkAllDone={() => handleMarkAllStoryTasksDone(story.issueid, children)}
                  isBusy={markingAllStoryIds.includes(story.issueid)}
                />
                {children.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                      marginLeft: 16,
                      paddingLeft: 14,
                      borderLeft: `2px solid ${C.accentSoft}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    {children.map((child) => (
                      <StoryChecklistTaskRow
                        key={child.issueid}
                        task={child}
                        onOpen={(id) => setSelectedId(String(id))}
                        onStatusChange={handleChecklistToggle}
                        isToggling={togglingTaskIds.includes(child.issueid)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {taskHierarchy.orphanTasks.map((row) => (
            <WorkItemCard
              key={row.issueid}
              task={row}
              onOpen={(id) => setSelectedId(String(id))}
              allowStatusChange
              onStatusChange={handleTaskStatusChange}
              workflowOptions={workflowOptions}
              department={boardDepartment}
            />
          ))}
          {taskHierarchy.other.map((row) => (
            <WorkItemCard
              key={row.issueid}
              task={row}
              onOpen={(id) => setSelectedId(String(id))}
              allowStatusChange
              onStatusChange={handleTaskStatusChange}
              workflowOptions={workflowOptions}
              department={boardDepartment}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {list.map((row) => (
            <WorkItemCard
              key={row.issueid}
              task={row}
              onOpen={(id) => setSelectedId(String(id))}
              onStatusChange={handleTaskStatusChange}
              workflowOptions={workflowOptions}
              department={boardDepartment}
            />
          ))}
        </div>
      )}

      {createMode === 'task' && (
        <WorkItemCreateModal
          open
          onClose={() => setCreateMode(null)}
          projectId={createTaskDefaults.projectId || undefined}
          sprintId={createTaskDefaults.sprintId || undefined}
          preferAssignedProjects
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['my-issues'] });
            selectWorkTab('tasks');
          }}
        />
      )}

      {createMode === 'bug' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div style={issueFormModalPanelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h3 style={{ margin: 0, color: C.ink }}>Log a Bug</h3>
              <button
                type="button"
                onClick={() => setCreateMode(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            <IssueForm
              key={`create-bug-${createTaskDefaults.projectId}-${createTaskDefaults.sprintId}`}
              fixedType="Bug"
              projectId={createTaskDefaults.projectId || undefined}
              sprintId={createTaskDefaults.sprintId || undefined}
              allowSprintChange
              autoGrowDescription
              onSuccess={() => {
                qc.invalidateQueries({ queryKey: ['my-issues'] });
                setCreateMode(null);
                toast.success('Bug logged!');
              }}
            />
          </div>
        </div>
      )}

      {selectedId && loadingSelectedIssue && !selectedIssue && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998,
          }}
        >
          <CircularProgress size={40} sx={{ color: '#fff' }} />
        </div>
      )}

      {selectedId && selectedIssue && isStoryOrTaskIssue(selectedIssue) && (
        <TaskDetailModal
          task={selectedIssue}
          onClose={() => {
            setSelectedId(null);
            setStoryReturnIssue(null);
          }}
          users={assignUsers}
          childTaskCount={
            selectedIssue.issuetype === 'Story' ? (hierarchyStoryChildren?.length ?? 0) : 0
          }
          childTasks={
            selectedIssue.issuetype === 'Story' ? hierarchyStoryChildren : []
          }
          onOpenChildTask={
            selectedIssue.issuetype === 'Story'
              ? (child) => {
                  setStoryReturnIssue(selectedIssue);
                  setSelectedId(String(child.issueid));
                }
              : undefined
          }
          onBackToParent={
            storyReturnIssue
              ? () => {
                  setSelectedId(String(storyReturnIssue.issueid));
                  setStoryReturnIssue(null);
                }
              : undefined
          }
          parentIssue={parentIssueForModal}
          projectId={selectedIssue.projectid || selectedIssue.project_id}
          sprintId={selectedIssue.sprint_id}
          onTaskCreated={() => {
            qc.invalidateQueries({ queryKey: ['my-issues'] });
            qc.invalidateQueries({ queryKey: ['issue', selectedId] });
            if (selectedIssue.issuetype === 'Story') {
              qc.invalidateQueries({ queryKey: ['story-subtasks', selectedIssue.issueid] });
            }
            if (storyReturnIssue?.issueid) {
              qc.invalidateQueries({ queryKey: ['story-subtasks', storyReturnIssue.issueid] });
              qc.invalidateQueries({ queryKey: ['issue', storyReturnIssue.issueid] });
            }
          }}
          navigationIds={workSubTab === 'tasks' ? taskNavigationIds : null}
          onNavigate={(id) => {
            setStoryReturnIssue(null);
            setSelectedId(String(id));
          }}
          canEdit={false}
          onStatusChange={() => {}}
          onReassign={() => {}}
        />
      )}

      {selectedId
        && selectedIssue
        && !isStoryOrTaskIssue(selectedIssue) && (
        <IssueDetailModal
          issueId={selectedId}
          users={assignUsers}
          onClose={() => setSelectedId(null)}
          onStatusChanged={handleIssueStatusChanged}
        />
      )}

      <PerformanceReviewModal
        open={prOpen}
        onClose={() => setPrOpen(false)}
        year={prCycleParams.year}
        quarter={prCycleParams.quarter}
        allowCycleSetup={canSetupPerformanceReviewCycle(role)}
      />

      <TeamSopViewDialog open={documentsOpen} onClose={() => setDocumentsOpen(false)} />
    </div>
  );
};

export default MyTasks;
